import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import './App.css';

// Layouts
import MainLayout from './layouts/MainLayout';

// Pages
import FinanceDashboard from './pages/FinanceDashboard';
import Expenses from './pages/Expenses';
import ExpenseForm from './pages/ExpenseForm';
import Income from './pages/Income';
import Reports from './pages/Reports';
import DailyClosing from './pages/DailyClosing';
import MonthlyClosing from './pages/MonthlyClosing';
import CommissionBreakdown from './pages/CommissionBreakdown';
import SalesSplitBreakdown from './pages/SalesSplitBreakdown';
import GLAccounts from './pages/GLAccounts';
import GLAccountTransactions from './pages/GLAccountTransactions';
import Journal from './pages/Journal';
import JournalEntry from './pages/JournalEntry';
import Invoices from './pages/Invoices';
import InvoiceEntry from './pages/InvoiceEntry';
import AdminUtilities from './pages/AdminUtilities';
import InventoryDashboard from './pages/InventoryDashboard';
import PurchaseReceiptHistory from './pages/PurchaseReceiptHistory';
import InventoryAdjustmentHistory from './pages/InventoryAdjustmentHistory';
import InventoryCOGS from './pages/InventoryCOGS';
// Trial Balance is now integrated into Reports page
import Login from './pages/Login';

// Context
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Create a theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#333333', // Dark gray
      light: '#555555',
      dark: '#222222',
    },
    secondary: {
      main: '#3f51b5', // Blue accent
    },
    background: {
      default: '#f8f8f8', // Light gray background
      paper: '#ffffff',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <AuthProvider>
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              
              {/* Protected routes with MainLayout */}
              <Route element={<ProtectedRoute />}>
                <Route element={<MainLayout />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<FinanceDashboard />} />
                  <Route path="/expenses" element={<Expenses />} />
                  <Route path="/income" element={<Income />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/daily-closing" element={<DailyClosing />} />
                  <Route path="/monthly-closing" element={<MonthlyClosing />} />
                  <Route path="/commission-breakdown" element={<CommissionBreakdown />} />
                  <Route path="/sales-split-breakdown" element={<SalesSplitBreakdown />} />
                  <Route path="/gl-accounts" element={<GLAccounts />} />
                  <Route path="/gl-account-transactions/:accountId" element={<GLAccountTransactions />} />
                  <Route path="/gl-account-transactions/:accountId/:startDate/:endDate" element={<GLAccountTransactions />} />
                  <Route path="/journal" element={<Journal />} />
                  <Route path="/journal-entry" element={<JournalEntry />} />
                  <Route path="/inventory" element={<InventoryDashboard />} />
                  <Route path="/purchase-receipts" element={<PurchaseReceiptHistory />} />
                  <Route path="/inventory-adjustments" element={<InventoryAdjustmentHistory />} />
                  <Route path="/inventory-cogs" element={<InventoryCOGS />} />
                  <Route path="/invoices" element={<Invoices />} />
                  <Route path="/invoices/new" element={<InvoiceEntry />} />
                  <Route path="/invoices/edit/:id" element={<InvoiceEntry />} />
                  <Route path="/admin-utilities" element={<AdminUtilities />} />
                  
                  {/* Add more routes as needed */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
